import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { parseEBUScoreCSV } from '../utils/ebuParser';
import { parsePBN } from '../utils/pbnParser';
import { parseUSEBIO, parseUSEBIODate } from '../utils/usebioParser';
import { parseBBOTeamMatch } from '../utils/bboParser';
import { getAllMappings, addPlayerMapping, deletePlayerMapping } from '../utils/playerNameMapper';
import JSZip from 'jszip';
import './Admin.css';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'bridge2026';

function Admin() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [activeSection, setActiveSection] = useState('single');
  
  // Single upload states
  const [csvFile, setCsvFile] = useState(null);
  const [pbnFile, setPbnFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [pbnFileName, setPbnFileName] = useState('');
  const [isSpecial, setIsSpecial] = useState(false);
  
  // Bulk upload states
  const [zipFile, setZipFile] = useState(null);
  const [pairingsFile, setPairingsFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [importResults, setImportResults] = useState([]);
  
  // Delete states
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);

  // BBO upload states
  const [bboUrl, setBboUrl] = useState('');
  const [bboHtml, setBboHtml] = useState('');

  // Player mappings states
  const [playerMappings, setPlayerMappings] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newActualName, setNewActualName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Load games when switching to delete tab
  useEffect(() => {
    if (authenticated && activeSection === 'delete') {
      loadGames();
    }
  }, [authenticated, activeSection]);

  // Load player mappings when switching to mappings tab
  useEffect(() => {
    if (authenticated && activeSection === 'mappings') {
      loadPlayerMappings();
    }
  }, [authenticated, activeSection]);

  function handleLogin(e) {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setMessage('');
      loadGames();
    } else {
      setMessage('Incorrect password');
    }
  }

  async function loadGames() {
    setLoadingGames(true);
    try {
      const { data, error } = await supabase
        .from('games')
        .select('id, title, game_date, game_type, is_special')
        .order('game_date', { ascending: false });

      if (error) throw error;
      console.log('Games loaded:', data);
      setGames(data || []);
    } catch (error) {
      console.error('Error loading games:', error);
    } finally {
      setLoadingGames(false);
    }
  }

  // ============ PLAYER MAPPINGS ============
  async function loadPlayerMappings() {
    setLoadingMappings(true);
    try {
      const mappings = await getAllMappings();
      setPlayerMappings(mappings);
    } catch (error) {
      console.error('Error loading player mappings:', error);
      setMessage('Error loading player mappings');
    } finally {
      setLoadingMappings(false);
    }
  }

  async function handleAddMapping(e) {
    e.preventDefault();

    if (!newUsername.trim() || !newActualName.trim()) {
      setMessage('Please enter both username and actual name');
      return;
    }

    try {
      const success = await addPlayerMapping(newUsername, newActualName, newNotes);
      if (success) {
        setMessage(`Added mapping: ${newUsername} → ${newActualName}`);
        setNewUsername('');
        setNewActualName('');
        setNewNotes('');
        await loadPlayerMappings();
      } else {
        setMessage('Failed to add mapping');
      }
    } catch (error) {
      console.error('Error adding mapping:', error);
      setMessage('Error adding mapping');
    }
  }

  async function handleDeleteMapping(username) {
    if (!window.confirm(`Delete mapping for "${username}"?`)) {
      return;
    }

    try {
      const success = await deletePlayerMapping(username);
      if (success) {
        setMessage(`Deleted mapping for ${username}`);
        await loadPlayerMappings();
      } else {
        setMessage('Failed to delete mapping');
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
      setMessage('Error deleting mapping');
    }
  }

  // ============ BBO UPLOAD ============
  async function handleBBOUpload(e) {
    e.preventDefault();

    if (!bboHtml) {
      setMessage('Please paste the HTML content from the BBO page');
      return;
    }

    setUploading(true);
    setMessage('Parsing BBO data...');

    try {
      // Parse BBO data
      const parsedData = await parseBBOTeamMatch(bboHtml, bboUrl);

      if (!parsedData || !parsedData.scores || parsedData.scores.length === 0) {
        setMessage('Could not parse BBO data. The page format may not be supported.');
        setUploading(false);
        return;
      }

      // Prepare game data
      const gameTitle = parsedData.metadata?.Title || 'BBO Team Match';
      const gameDate = parsedData.metadata?.Date
        ? parseDate(parsedData.metadata.Date)
        : new Date().toISOString().split('T')[0];

      setMessage('Saving to database...');

      // Check if game exists
      const { data: existing } = await supabase
        .from('games')
        .select('id')
        .eq('title', gameTitle)
        .eq('game_date', gameDate)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('games')
          .update({
            game_type: 'teams',
            num_boards: parseInt(parsedData.metadata?.Boards) || 0,
            scores: parsedData.scores,
            travellers: parsedData.travellers,
            hand_records: parsedData.handRecords,
            metadata: parsedData.metadata
          })
          .eq('id', existing.id);

        if (error) throw error;
        setMessage(`Game "${gameTitle}" updated successfully! ${parsedData.rawMatchInfo?.numBoards || 0} boards imported.`);
      } else {
        const { error } = await supabase
          .from('games')
          .insert([{
            title: gameTitle,
            game_type: 'teams',
            game_date: gameDate,
            num_boards: parseInt(parsedData.metadata?.Boards) || 0,
            scores: parsedData.scores,
            travellers: parsedData.travellers,
            hand_records: parsedData.handRecords,
            metadata: parsedData.metadata
          }]);

        if (error) throw error;
        setMessage(`Game "${gameTitle}" created successfully! ${parsedData.rawMatchInfo?.numBoards || 0} boards imported.`);
      }

      // Clear form
      setBboUrl('');
      setBboHtml('');
      loadGames();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      console.error('BBO upload error:', error);
    } finally {
      setUploading(false);
    }
  }

  // ============ SINGLE UPLOAD ============
  async function handleSingleUpload(e) {
    e.preventDefault();

    if (!csvFile) {
      setMessage('Please select a CSV or XML file');
      return;
    }

    setUploading(true);
    setMessage('Processing files...');

    try {
      const fileText = await csvFile.text();
      const fileName = csvFile.name.toLowerCase();

      // Determine file type and parse accordingly
      let parsedData;
      let handRecords = null;

      if (fileName.endsWith('.xml')) {
        // Parse XML (USEBIO format)
        parsedData = await parseUSEBIO(fileText);
        // XML already includes hand records if available
        handRecords = parsedData.handRecords || null;
      } else {
        // Parse CSV (EBUScore format)
        parsedData = await parseEBUScoreCSV(fileText);

        // Parse separate PBN file for hand records if provided
        if (pbnFile) {
          const pbnText = await pbnFile.text();
          handRecords = parsePBN(pbnText);
        }
      }

      const gameTitle = parsedData.metadata?.Title || 'Untitled Game';
      const gameDate = parsedData.metadata?.Date
        ? (fileName.endsWith('.xml') ? parseUSEBIODate(parsedData.metadata.Date) : parseDate(parsedData.metadata.Date))
        : new Date().toISOString().split('T')[0];

      const { data: existing } = await supabase
        .from('games')
        .select('id')
        .eq('title', gameTitle)
        .eq('game_date', gameDate)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('games')
          .update({
            game_type: parsedData.gameType,
            num_boards: parseInt(parsedData.metadata?.Boards) || 0,
            director: parsedData.metadata?.Director || null,
            is_special: isSpecial,
            scores: parsedData.scores,
            travellers: parsedData.travellers,
            hand_records: handRecords,
            metadata: parsedData.metadata
          })
          .eq('id', existing.id);

        if (error) throw error;
        setMessage(`Game "${gameTitle}" updated successfully!`);
      } else {
        const { error } = await supabase
          .from('games')
          .insert([{
            title: gameTitle,
            game_type: parsedData.gameType,
            game_date: gameDate,
            num_boards: parseInt(parsedData.metadata?.Boards) || 0,
            director: parsedData.metadata?.Director || null,
            is_special: isSpecial,
            scores: parsedData.scores,
            travellers: parsedData.travellers,
            hand_records: handRecords,
            metadata: parsedData.metadata
          }]);

        if (error) throw error;
        setMessage(`Game "${gameTitle}" uploaded successfully!`);
      }

      setCsvFile(null);
      setPbnFile(null);
      setCsvFileName('');
      setPbnFileName('');
      setIsSpecial(false);
      loadGames();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  // ============ BULK UPLOAD ============
  function parseDate(dateStr) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  async function handleBulkPreview(e) {
    e.preventDefault();
    
    if (!zipFile || !pairingsFile) {
      setMessage('Please select both ZIP file and pairings CSV');
      return;
    }

    setMessage('Processing files...');
    
    try {
      const pairingsText = await pairingsFile.text();
      const pairingsLines = pairingsText.split('\n').slice(1);
      const pairings = [];
      
      for (const line of pairingsLines) {
        if (line.trim()) {
          const [csvFile, pbnFile] = line.split(',').map(s => s.trim());
          if (csvFile && pbnFile) {
            pairings.push({ csvFile, pbnFile });
          }
        }
      }

      const zip = await JSZip.loadAsync(zipFile);
      const previewData = [];
      
      for (const pairing of pairings) {
        try {
          const csvPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.csvFile)
          );

          const pbnPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.pbnFile)
          );

          if (!csvPath) {
            previewData.push({
              csvFile: pairing.csvFile,
              pbnFile: pairing.pbnFile,
              status: 'error',
              error: 'Result file not found in ZIP'
            });
            continue;
          }

          const fileText = await zip.files[csvPath].async('text');
          const fileName = pairing.csvFile.toLowerCase();

          // Parse based on file type
          let parsedData;
          let boardCount = 0;

          if (fileName.endsWith('.xml')) {
            // Parse XML
            parsedData = await parseUSEBIO(fileText);
            boardCount = Object.keys(parsedData.travellers).length;
          } else {
            // Parse CSV
            parsedData = await parseEBUScoreCSV(fileText);

            if (pbnPath) {
              const pbnText = await zip.files[pbnPath].async('text');
              const handRecords = parsePBN(pbnText);
              boardCount = Object.keys(handRecords).length;
            } else {
              boardCount = Object.keys(parsedData.travellers).length;
            }
          }

          const gameName = parsedData.metadata?.Title || 'Untitled';
          const gameDate = parsedData.metadata?.Date
            ? (fileName.endsWith('.xml') ? parseUSEBIODate(parsedData.metadata.Date) : parseDate(parsedData.metadata.Date))
            : 'N/A';
          
          previewData.push({
            csvFile: pairing.csvFile,
            pbnFile: pairing.pbnFile,
            status: 'ready',
            gameName: gameName,
            gameType: parsedData.gameType,
            date: gameDate,
            players: parsedData.scores.length,
            boards: boardCount || Object.keys(parsedData.travellers).length
          });
        } catch (error) {
          previewData.push({
            csvFile: pairing.csvFile,
            pbnFile: pairing.pbnFile,
            status: 'error',
            error: error.message
          });
        }
      }
      
      setPreview(previewData);
      setMessage(`Found ${previewData.length} games. Ready to import.`);
    } catch (error) {
      setMessage(`Error processing files: ${error.message}`);
    }
  }

  async function handleBulkImport() {
    if (preview.length === 0) {
      setMessage('Please preview files first');
      return;
    }

    setUploading(true);
    setMessage('Starting bulk import...');
    setImportResults([]);
    
    const results = [];
    
    try {
      const zip = await JSZip.loadAsync(zipFile);
      const pairingsText = await pairingsFile.text();
      const pairingsLines = pairingsText.split('\n').slice(1);
      const pairings = [];
      
      for (const line of pairingsLines) {
        if (line.trim()) {
          const [csvFile, pbnFile] = line.split(',').map(s => s.trim());
          if (csvFile && pbnFile) {
            pairings.push({ csvFile, pbnFile });
          }
        }
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < pairings.length; i++) {
        const pairing = pairings[i];
        setMessage(`Importing ${i + 1}/${pairings.length}: ${pairing.csvFile}...`);
        
        try {
          const csvPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.csvFile)
          );
          const pbnPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.pbnFile)
          );

          if (!csvPath) {
            results.push({
              csvFile: pairing.csvFile,
              status: 'error',
              error: 'Result file not found'
            });
            errorCount++;
            continue;
          }

          const fileText = await zip.files[csvPath].async('text');
          const fileName = pairing.csvFile.toLowerCase();

          // Parse based on file type
          let parsedData;
          let handRecords = null;

          if (fileName.endsWith('.xml')) {
            // Parse XML
            parsedData = await parseUSEBIO(fileText);
            handRecords = parsedData.handRecords || null;
          } else {
            // Parse CSV
            parsedData = await parseEBUScoreCSV(fileText);

            if (pbnPath) {
              const pbnText = await zip.files[pbnPath].async('text');
              handRecords = parsePBN(pbnText);
            }
          }

          const gameName = parsedData.metadata?.Title || 'Untitled Game';
          const gameDate = parsedData.metadata?.Date
            ? (fileName.endsWith('.xml') ? parseUSEBIODate(parsedData.metadata.Date) : parseDate(parsedData.metadata.Date))
            : new Date().toISOString().split('T')[0];
          
          const { data: existing } = await supabase
            .from('games')
            .select('id')
            .eq('title', gameName)
            .eq('game_date', gameDate)
            .single();

          if (existing) {
            const { error } = await supabase
              .from('games')
              .update({
                game_type: parsedData.gameType,
                num_boards: parseInt(parsedData.metadata?.Boards) || 0,
                director: parsedData.metadata?.Director || null,
                scores: parsedData.scores,
                travellers: parsedData.travellers,
                hand_records: handRecords,
                metadata: parsedData.metadata
              })
              .eq('id', existing.id);
            
            if (error) throw error;

            results.push({
              csvFile: pairing.csvFile,
              status: 'updated',
              gameName: gameName
            });
          } else {
            const { error } = await supabase
              .from('games')
              .insert([{
                title: gameName,
                game_type: parsedData.gameType,
                game_date: gameDate,
                num_boards: parseInt(parsedData.metadata?.Boards) || 0,
                director: parsedData.metadata?.Director || null,
                scores: parsedData.scores,
                travellers: parsedData.travellers,
                hand_records: handRecords,
                metadata: parsedData.metadata
              }])
              .select('id')
              .single();
            
            if (error) throw error;
            
            results.push({
              csvFile: pairing.csvFile,
              status: 'created',
              gameName: gameName
            });
          }
          
          successCount++;
        } catch (error) {
          results.push({
            csvFile: pairing.csvFile,
            status: 'error',
            error: error.message
          });
          errorCount++;
        }
        
        setImportResults([...results]);
      }
      
      setMessage(`Import complete! ${successCount} succeeded, ${errorCount} failed.`);
      loadGames();
    } catch (error) {
      setMessage(`Bulk import error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  // ============ DELETE ============
  async function handleDeleteGame(gameId, gameTitle) {
    if (!window.confirm(`Are you sure you want to delete "${gameTitle}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', gameId);

      if (error) throw error;
      
      setMessage(`Game "${gameTitle}" deleted successfully`);
      loadGames();
    } catch (error) {
      setMessage(`Error deleting game: ${error.message}`);
    }
  }

  async function handleToggleSpecial(gameId, newSpecialValue) {
    try {
      const { error } = await supabase
        .from('games')
        .update({ is_special: newSpecialValue })
        .eq('id', gameId);

      if (error) throw error;
      
      setMessage(`Game ${newSpecialValue ? 'marked as special' : 'unmarked as special'}`);
      loadGames();
    } catch (error) {
      setMessage(`Error updating game: ${error.message}`);
    }
  }

  async function handleDeleteAll() {
    if (!window.confirm('⚠️ WARNING: This will delete ALL games from the database. Are you absolutely sure?')) {
      return;
    }
    
    if (!window.confirm('This action CANNOT be undone! Type YES in the next prompt to confirm.')) {
      return;
    }
    
    const confirmation = window.prompt('Type YES to confirm deletion of all games:');
    if (confirmation !== 'YES') {
      setMessage('Deletion cancelled');
      return;
    }

    setUploading(true);
    setMessage('Deleting all games...');

    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) throw error;
      
      setMessage('All games deleted successfully');
      loadGames();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  if (!authenticated) {
    return (
      <div className="admin-page">
        <h2>Admin Access</h2>
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit">Login</button>
        </form>
        {message && <p className="message error">{message}</p>}
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h2>Admin Dashboard</h2>
      
      <div className="admin-nav">
        <button
          className={activeSection === 'single' ? 'active' : ''}
          onClick={() => setActiveSection('single')}
        >
          Single Upload
        </button>
        <button
          className={activeSection === 'bbo' ? 'active' : ''}
          onClick={() => setActiveSection('bbo')}
        >
          BBO URL Upload
        </button>
        <button
          className={activeSection === 'bulk' ? 'active' : ''}
          onClick={() => setActiveSection('bulk')}
        >
          Bulk Upload
        </button>
        <button
          className={activeSection === 'delete' ? 'active' : ''}
          onClick={() => setActiveSection('delete')}
        >
          Manage Games
        </button>
        <button
          className={activeSection === 'mappings' ? 'active' : ''}
          onClick={() => setActiveSection('mappings')}
        >
          Player Names
        </button>
      </div>

      {/* BBO UPLOAD */}
      {activeSection === 'bbo' && (
        <div className="admin-section">
          <h3>BBO Team Match Upload</h3>
          <p className="section-description">
            Upload team match results from Bridge Base Online by pasting the HTML source.
          </p>

          <form onSubmit={handleBBOUpload}>
            <div className="file-input-group">
              <label>BBO Team Match URL (optional, for reference):</label>
              <input
                type="text"
                placeholder="https://webutil.bridgebase.com/v2/tview.php?t=..."
                value={bboUrl}
                onChange={(e) => setBboUrl(e.target.value)}
                className="bbo-url-input"
              />
              <small className="help-text">
                Example: https://webutil.bridgebase.com/v2/tview.php?t=99436-1768737789&u=likeyj
              </small>
            </div>

            <div className="file-input-group">
              <label>Paste HTML Content:</label>
              <textarea
                placeholder="Paste the full HTML source code from the BBO page here...&#10;&#10;To get HTML:&#10;1. Open the BBO URL in your browser&#10;2. Right-click → View Page Source (or press Ctrl+U)&#10;3. Copy all the HTML&#10;4. Paste it here"
                value={bboHtml}
                onChange={(e) => setBboHtml(e.target.value)}
                rows="15"
                className="bbo-html-input"
              />
              <small className="help-text">
                <strong>How to get HTML:</strong><br />
                1. Open the BBO team match URL in your browser<br />
                2. Right-click on the page → "View Page Source" (or press Ctrl+U / Cmd+Option+U)<br />
                3. Select all HTML (Ctrl+A / Cmd+A), then copy (Ctrl+C / Cmd+C)<br />
                4. Paste it in the box above
              </small>
            </div>

            <button type="submit" disabled={uploading || !bboHtml}>
              {uploading ? 'Processing...' : 'Import from BBO'}
            </button>
          </form>

          <div className="bbo-help-section">
            <h4>📖 Instructions</h4>
            <ol>
              <li><strong>Get the BBO URL:</strong> Copy the team match URL from bridgebase.com (tview.php format)</li>
              <li><strong>Choose method:</strong>
                <ul>
                  <li><strong>Fetch URL:</strong> Tries to fetch automatically (may fail due to browser security)</li>
                  <li><strong>Paste HTML (recommended):</strong> View page source, copy HTML, paste it here</li>
                </ul>
              </li>
              <li><strong>Import:</strong> Click "Import from BBO" to process the data</li>
            </ol>

            <h4>⚠️ Important Notes</h4>
            <ul>
              <li>Only team match pages (tview.php) are currently supported</li>
              <li>Hand diagrams are not automatically fetched (would require separate API)</li>
              <li>Some BBO pages may require login to access</li>
              <li>The "Paste HTML" method is more reliable than automatic fetching</li>
            </ul>

            <h4>🔧 Alternative: Python Scripts</h4>
            <p>
              For advanced usage with authentication and hand diagram fetching, use the Python scripts
              in the repository root: <code>bbo_scraper.py</code>, <code>bbo_to_app_converter.py</code>
            </p>
          </div>
        </div>
      )}

      {/* SINGLE UPLOAD */}
      {activeSection === 'single' && (
        <div className="admin-section">
          <h3>Single Game Upload</h3>
          <form onSubmit={handleSingleUpload}>
            <div className="file-input-group">
              <label>Result File (required - CSV or XML):</label>
              <input
                type="file"
                accept=".csv,.xml"
                onChange={(e) => {
                  setCsvFile(e.target.files[0]);
                  setCsvFileName(e.target.files[0]?.name || '');
                }}
              />
              {csvFileName && <span className="file-name">{csvFileName}</span>}
            </div>

            <div className="file-input-group">
              <label>PBN File (optional - only for CSV uploads):</label>
              <input
                type="file"
                accept=".pbn"
                onChange={(e) => {
                  setPbnFile(e.target.files[0]);
                  setPbnFileName(e.target.files[0]?.name || '');
                }}
                disabled={csvFileName.toLowerCase().endsWith('.xml')}
              />
              {pbnFileName && <span className="file-name">{pbnFileName}</span>}
              {csvFileName.toLowerCase().endsWith('.xml') && <span className="file-note">XML files include hand records</span>}
            </div>

            <div className="file-input-group">
              <label>
                <input
                  type="checkbox"
                  checked={isSpecial}
                  onChange={(e) => setIsSpecial(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                Mark as Special Event ⭐
              </label>
            </div>

            <button type="submit" disabled={!csvFile || uploading}>
              {uploading ? 'Uploading...' : 'Upload Game'}
            </button>
          </form>
        </div>
      )}

      {/* BULK UPLOAD */}
      {activeSection === 'bulk' && (
        <div className="admin-section">
          <h3>Bulk Upload</h3>
          
          <div className="file-inputs">
            <div className="file-input-group">
              <label>ZIP File (containing CSVs/XMLs and PBNs):</label>
              <input
                type="file"
                accept=".zip"
                onChange={(e) => setZipFile(e.target.files[0])}
              />
              {zipFile && <span className="file-name">{zipFile.name}</span>}
            </div>

            <div className="file-input-group">
              <label>Pairings CSV (Result_File,PBN_File - PBN optional for XML):</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setPairingsFile(e.target.files[0])}
              />
              {pairingsFile && <span className="file-name">{pairingsFile.name}</span>}
            </div>
          </div>
          
          <button 
            onClick={handleBulkPreview} 
            disabled={!zipFile || !pairingsFile || uploading}
            className="preview-btn"
          >
            Preview Games
          </button>
          
          {preview.length > 0 && (
            <div className="preview-section">
              <h4>Preview ({preview.length} games)</h4>
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>CSV File</th>
                      <th>PBN File</th>
                      <th>Game Name</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Players</th>
                      <th>Boards</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((item, idx) => (
                      <tr key={idx} className={item.status}>
                        <td>{item.csvFile}</td>
                        <td>{item.pbnFile}</td>
                        <td>{item.gameName || '-'}</td>
                        <td>{item.gameType || '-'}</td>
                        <td>{item.date || '-'}</td>
                        <td>{item.players || '-'}</td>
                        <td>{item.boards || '-'}</td>
                        <td>
                          {item.status === 'ready' && <span className="status-ready">✓ Ready</span>}
                          {item.status === 'error' && <span className="status-error">✗ {item.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <button 
                onClick={handleBulkImport} 
                disabled={uploading || preview.filter(p => p.status === 'ready').length === 0}
                className="import-btn"
              >
                {uploading ? 'Importing...' : `Import ${preview.filter(p => p.status === 'ready').length} Games`}
              </button>
            </div>
          )}
          
          {importResults.length > 0 && (
            <div className="results-section">
              <h4>Import Results</h4>
              <div className="results-table-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>CSV File</th>
                      <th>Game Name</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResults.map((result, idx) => (
                      <tr key={idx} className={result.status}>
                        <td>{result.csvFile}</td>
                        <td>{result.gameName || '-'}</td>
                        <td>
                          {result.status === 'created' && <span className="status-created">✓ Created</span>}
                          {result.status === 'updated' && <span className="status-updated">↻ Updated</span>}
                          {result.status === 'error' && <span className="status-error">✗ {result.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DELETE / MANAGE */}
      {activeSection === 'delete' && (
        <div className="admin-section">
          <h3>Manage Games</h3>
          
          <div className="games-list">
            {loadingGames ? (
              <p>Loading games...</p>
            ) : games.length === 0 ? (
              <p>No games found</p>
            ) : (
              <div className="admin-games-grid">
                {games.map(game => (
                  <div key={game.id} className="admin-game-card">
                    <div className="game-info">
                      <h4>
                        {game.title}
                        {game.is_special && <span className="special-star"> ⭐</span>}
                      </h4>
                      <div className="game-meta">
                        <span className="game-date">{game.game_date}</span>
                        <span className={`game-type ${game.game_type}`}>
                          {game.game_type === 'teams' ? 'Teams' : 'Pairs'}
                        </span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className={`special-toggle-btn ${game.is_special ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleSpecial(game.id, !game.is_special);
                        }}
                        title={game.is_special ? 'Remove Special' : 'Mark as Special'}
                      >
                        {game.is_special ? '⭐ Special' : '☆ Mark Special'}
                      </button>
                      <button 
                        className="delete-event-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteGame(game.id, game.title);
                        }}
                      >
                        Delete Event
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="danger-zone">
            <h4>⚠️ Danger Zone</h4>
            <p>Delete all games from the database. This action cannot be undone.</p>
            <button
              onClick={handleDeleteAll}
              disabled={uploading}
              className="delete-all-btn"
            >
              Delete All Games
            </button>
          </div>
        </div>
      )}

      {/* PLAYER NAME MAPPINGS */}
      {activeSection === 'mappings' && (
        <div className="admin-section">
          <h3>Player Name Mappings</h3>
          <p className="section-description">
            Map BBO usernames to actual player names. Usernames are case-insensitive.
            When a username is found in any uploaded data, it will be automatically replaced with the actual name.
          </p>

          <div className="mappings-container">
            <div className="add-mapping-form">
              <h4>Add New Mapping</h4>
              <form onSubmit={handleAddMapping}>
                <div className="form-row">
                  <div className="form-group">
                    <label>BBO Username:</label>
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="e.g., lcy, xhxhxh, high325"
                      className="mapping-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Actual Name:</label>
                    <input
                      type="text"
                      value={newActualName}
                      onChange={(e) => setNewActualName(e.target.value)}
                      placeholder="e.g., Cheng Yen Lam"
                      className="mapping-input"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes (optional):</label>
                  <input
                    type="text"
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="e.g., Initial mapping, Club member"
                    className="mapping-input"
                  />
                </div>
                <button type="submit" className="btn-primary">
                  Add Mapping
                </button>
              </form>
            </div>

            <div className="mappings-list">
              <h4>Current Mappings ({playerMappings.length})</h4>
              {loadingMappings ? (
                <p>Loading mappings...</p>
              ) : playerMappings.length === 0 ? (
                <p className="no-mappings">No player mappings yet. Add some above!</p>
              ) : (
                <table className="mappings-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Actual Name</th>
                      <th>Notes</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {playerMappings.map((mapping) => (
                      <tr key={mapping.id}>
                        <td className="username-cell">{mapping.username}</td>
                        <td className="name-cell">{mapping.actual_name}</td>
                        <td className="notes-cell">{mapping.notes || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleDeleteMapping(mapping.username)}
                            className="btn-delete-small"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="mappings-info">
            <h4>ℹ️ How it works</h4>
            <ul>
              <li><strong>Case-insensitive matching:</strong> "lcy", "LCY", and "Lcy" all match the same mapping</li>
              <li><strong>Automatic replacement:</strong> All parsers (BBO, EBU, USEBIO) apply mappings automatically</li>
              <li><strong>Persistent storage:</strong> Mappings are stored in the database and persist across sessions</li>
              <li><strong>Update existing:</strong> Adding a mapping with an existing username will update it</li>
            </ul>
          </div>
        </div>
      )}

      {message && <p className="message">{message}</p>}
    </div>
  );
}

export default Admin;
